import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { cities94 } from "@/mocks/cities94";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";
const PHONE = "0604026840";
const PHONE_DISPLAY = "06 04 02 68 40";
const WHATSAPP_URL = `https://wa.me/33604026840?text=Bonjour%2C%20j%27ai%20besoin%20d%27un%20serrurier%20en%20urgence`;

const SERVICES = [
  { icon: "ri-door-open-line", label: "Ouverture de porte", desc: "Porte claquée, clé perdue, serrure bloquée — intervention immédiate." },
  { icon: "ri-lock-2-line", label: "Changement de serrure", desc: "Remplacement multipoints, serrure connectée, cylindre haute sécurité." },
  { icon: "ri-shield-check-line", label: "Blindage de porte", desc: "Renforcement anti-effraction, pose de verrous supplémentaires." },
  { icon: "ri-car-line", label: "Ouverture de voiture", desc: "Clé enfermée dans le véhicule, ouverture sans dommage garantie." },
  { icon: "ri-window-line", label: "Réparation de volets", desc: "Volet roulant bloqué, lame cassée, motorisation défaillante." },
  { icon: "ri-tools-line", label: "Dépannage urgent", desc: "Disponible 24h/24 et 7j/7, même les jours fériés." },
];

const FAQS = (cityName: string) => [
  {
    q: `Quel est le délai d'intervention d'un serrurier à ${cityName} ?`,
    a: `Securycles intervient en moyenne en 20 minutes à ${cityName}. Nos techniciens sont positionnés dans le Val-de-Marne pour garantir une réponse rapide à toute urgence serrurerie.`,
  },
  {
    q: `Combien coûte une ouverture de porte à ${cityName} ?`,
    a: `Le tarif d'une ouverture de porte à ${cityName} dépend du type de serrure et de l'heure d'intervention. Nous pratiquons des tarifs transparents avec devis gratuit avant toute intervention.`,
  },
  {
    q: `Intervenez-vous la nuit et le week-end à ${cityName} ?`,
    a: `Oui, Securycles est disponible 24h/24 et 7j/7 à ${cityName}, y compris les nuits, week-ends et jours fériés. L'urgence n'attend pas.`,
  },
  {
    q: `Quels types de serrures posez-vous à ${cityName} ?`,
    a: `Nous posons toutes marques : Fichet, Vachette, Mul-T-Lock, Abloy, ainsi que des serrures connectées. Nos serruriers sont formés aux dernières technologies de sécurité.`,
  },
];

export default function CityPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const city = cities94.find((c) => c.slug === slug);

  useEffect(() => {
    if (!city) {
      navigate("/", { replace: true });
      return;
    }
    window.scrollTo(0, 0);

    document.title = `Serrurier ${city.name} (${city.zip}) — Urgence 24h/24 | Securycles`;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", `Serrurier urgence à ${city.name} (${city.zip}) — Intervention en ${city.time}. Ouverture de porte, changement de serrure, dépannage 24h/24 7j/7. Devis gratuit. Appelez le ${PHONE_DISPLAY}.`);
    setMeta("keywords", `serrurier ${city.name}, serrurier urgence ${city.zip}, ouverture porte ${city.name}, serrurier Val-de-Marne`);
    setMeta("robots", "index, follow");
    setMeta("last-modified", "2026-04-28");

    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link") as HTMLLinkElement;
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/serrurier/${city.slug}`;

    // Open Graph
    setMeta("og:type", "website", true);
    setMeta("og:title", `Serrurier ${city.name} (${city.zip}) — Urgence 24h/24 | Securycles`, true);
    setMeta("og:description", `Serrurier urgence à ${city.name} (${city.zip}) — Intervention en ${city.time}. Ouverture de porte, changement de serrure, dépannage 24h/24 7j/7.`, true);
    setMeta("og:url", `${SITE_URL}/serrurier/${city.slug}`, true);
    setMeta("og:locale", "fr_FR", true);
    setMeta("og:site_name", "Securycles", true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `Serrurier ${city.name} (${city.zip}) — Urgence 24h/24 | Securycles`);
    setMeta("twitter:description", `Serrurier urgence à ${city.name} (${city.zip}) — Intervention en ${city.time}. Appelez le ${PHONE_DISPLAY}.`);

    // Schema.org LocalBusiness for city
    const schemaId = "schema-city-page";
    let existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Locksmith",
      "name": `Securycles — Serrurier ${city.name}`,
      "description": `Serrurier d'urgence à ${city.name} (${city.zip}). Ouverture de porte, changement de serrure, dépannage auto. Disponible 24h/24 et 7j/7.`,
      "url": `${SITE_URL}/serrurier/${city.slug}`,
      "telephone": "+33604026840",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "postalCode": city.zip,
        "addressRegion": "Val-de-Marne",
        "addressCountry": "FR"
      },
      "areaServed": {
        "@type": "AdministrativeArea",
        "name": city.name
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      ],
      "priceRange": "€€",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "87",
        "bestRating": "5"
      }
    });
    document.head.appendChild(script);

    // Schema.org FAQPage
    const faqSchemaId = "schema-city-faq";
    let faqExisting = document.getElementById(faqSchemaId);
    if (faqExisting) faqExisting.remove();
    const faqScript = document.createElement("script");
    faqScript.id = faqSchemaId;
    faqScript.type = "application/ld+json";
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS(city.name).map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    });
    document.head.appendChild(faqScript);

    // Schema.org BreadcrumbList
    const bcSchemaId = "schema-city-breadcrumb";
    let bcExisting = document.getElementById(bcSchemaId);
    if (bcExisting) bcExisting.remove();
    const bcScript = document.createElement("script");
    bcScript.id = bcSchemaId;
    bcScript.type = "application/ld+json";
    bcScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": `Serrurier ${city.name}`,
          "item": `${SITE_URL}/serrurier/${city.slug}`
        }
      ]
    });
    document.head.appendChild(bcScript);

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
      const fs = document.getElementById(faqSchemaId);
      if (fs) fs.remove();
      const bcs = document.getElementById(bcSchemaId);
      if (bcs) bcs.remove();
    };
  }, [city, navigate]);

  if (!city) return null;

  const faqs = FAQS(city.name);
  const nearbyCount = Math.min(6, cities94.filter((c) => c.slug !== slug).length);
  const nearby = cities94.filter((c) => c.slug !== slug).slice(0, nearbyCount);

  return (
    <>
      <main>
        {/* Hero */}
        <section className="relative min-h-[480px] md:min-h-[560px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={`https://readdy.ai/api/search-image?query=urban%20street%20view%20of%20a%20French%20suburb%20residential%20neighborhood%20at%20golden%20hour%2C%20warm%20lighting%2C%20apartment%20buildings%2C%20parked%20cars%2C%20clean%20streets%2C%20professional%20photography%2C%20cinematic%20style%2C%20Val-de-Marne%20Paris%20suburb%20France&width=1920&height=800&seq=city-hero-${city.slug}&orientation=landscape`}
              alt={`Serrurier ${city.name}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/70"></div>
          </div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pt-28 pb-16 text-center">
            <div className="inline-flex items-center gap-2 bg-[#FFB100] text-black text-xs font-black px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
              <i className="ri-map-pin-line"></i>
              {city.zip} — Val-de-Marne
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4 uppercase tracking-tight">
              Serrurier à<br />
              <span className="text-[#FFB100]">{city.name}</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg font-light mb-8 max-w-xl mx-auto">
              Intervention en <strong className="text-[#FFB100] font-bold">{city.time}</strong> — Disponible 24h/24 et 7j/7 — Devis gratuit
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <a
                href={`tel:${PHONE}`}
                className="flex-1 flex items-center justify-center gap-2 bg-[#FFB100] text-black font-black text-sm md:text-base py-3 md:py-4 rounded-full hover:bg-yellow-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                Appeler le {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-white/10 border-2 border-white text-white font-bold text-sm md:text-base py-3 md:py-4 rounded-full hover:bg-white hover:text-black transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-[#FFB100] transition-colors cursor-pointer">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 font-medium">Serrurier {city.name}</span>
          </div>
        </div>

        {/* Intro + stats */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-black text-black uppercase leading-tight mb-5">
                  Votre serrurier de confiance<br />
                  <span className="text-[#FFB100]">à {city.name}</span>
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  Securycles est votre serrurier d&apos;urgence à {city.name} ({city.zip}). Que vous soyez victime d&apos;une porte claquée, d&apos;une serrure forcée ou d&apos;un cambriolage, nos techniciens certifiés interviennent en {city.time} à votre domicile ou sur votre lieu de travail.
                </p>
                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  Nous intervenons sur tous types de serrures et de portes : appartements, maisons, locaux commerciaux, caves et garages. Nos tarifs sont transparents et communiqués avant toute intervention.
                </p>
                <div className="flex flex-wrap gap-3">
                  {["Devis gratuit", "Intervention " + city.time, "Disponible 24h/24", "Techniciens certifiés"].map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1.5 bg-[#FFB100]/10 text-black text-xs font-bold px-3 py-1.5 rounded-full">
                      <i className="ri-check-line text-[#FFB100]"></i>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: "ri-time-line", value: city.time, label: "Délai d'intervention" },
                  { icon: "ri-star-fill", value: "4.9/5", label: "Note Google" },
                  { icon: "ri-shield-check-line", value: "100%", label: "Devis transparent" },
                  { icon: "ri-24-hours-line", value: "24h/7j", label: "Disponibilité" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-[#F8F8F8] rounded-2xl p-5 text-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FFB100] rounded-xl mx-auto mb-3">
                      <i className={`${stat.icon} text-black text-lg`}></i>
                    </div>
                    <p className="text-2xl font-black text-black mb-1">{stat.value}</p>
                    <p className="text-gray-500 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 md:py-20 bg-[#F8F8F8]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#FFB100] text-black text-xs font-black px-3 py-1.5 rounded-full mb-4 uppercase tracking-widest">
                <i className="ri-tools-line"></i>
                Nos prestations
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-black uppercase leading-tight">
                Services de serrurerie<br />
                <span className="text-[#FFB100]">à {city.name}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SERVICES.map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#FFB100] transition-all group">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFB100]/10 rounded-xl mb-4 group-hover:bg-[#FFB100] transition-colors">
                    <i className={`${s.icon} text-[#FFB100] text-xl group-hover:text-black transition-colors`}></i>
                  </div>
                  <h3 className="font-black text-black text-base mb-2">{s.label}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-black">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-black text-white uppercase mb-3">
              Besoin d&apos;un serrurier<br />
              <span className="text-[#FFB100]">à {city.name} maintenant ?</span>
            </h2>
            <p className="text-white/60 text-sm mb-8">Intervention en {city.time} — Devis gratuit — Disponible 24h/24</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
              <a
                href={`tel:${PHONE}`}
                className="flex-1 flex items-center justify-center gap-2 bg-[#FFB100] text-black font-black text-sm py-4 rounded-full hover:bg-yellow-400 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 border-2 border-white text-white font-bold text-sm py-4 rounded-full hover:bg-white hover:text-black transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-black text-black uppercase">
                Questions fréquentes —{" "}
                <span className="text-[#FFB100]">{city.name}</span>
              </h2>
            </div>
            <div className="flex flex-col gap-4">
              {faqs.map((faq, i) => (
                <FaqItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Villes proches */}
        <section className="py-14 bg-[#F8F8F8]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-black text-black uppercase mb-6">
              Serrurier dans les villes proches
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {nearby.map((c) => (
                <Link
                  key={c.slug}
                  to={`/serrurier/${c.slug}`}
                  className="bg-white rounded-xl p-4 border border-gray-100 hover:border-[#FFB100] hover:-translate-y-0.5 transition-all group cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-[#FFB100]/10 rounded-lg mb-2 group-hover:bg-[#FFB100] transition-colors">
                    <i className="ri-map-pin-2-line text-[#FFB100] text-xs group-hover:text-black transition-colors"></i>
                  </div>
                  <p className="font-bold text-black text-xs leading-tight mb-0.5">{c.name}</p>
                  <p className="text-[#FFB100] text-xs font-semibold">{c.time}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
      >
        <span className="font-bold text-black text-sm pr-4">{question}</span>
        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
          <i className={`text-[#FFB100] text-lg transition-transform duration-200 ${open ? "ri-subtract-line" : "ri-add-line"}`}></i>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed border-t border-gray-50">
          {answer}
        </div>
      )}
    </div>
  );
}
